/**
 * Whether this browser can group faces at all, and whether it should start now.
 *
 * Two separate questions, deliberately. *Can* is a hard capability check with a
 * yes or no answer. *Should* is a judgement about somebody's data allowance,
 * and getting it wrong costs them money rather than breaking anything.
 */

/**
 * WebAssembly SIMD, which the runtime requires and does not degrade without.
 *
 * ONNX Runtime stopped shipping a non-SIMD build, so a browser without it
 * cannot load the runtime at all — it fails inside the WebAssembly instantiate
 * with a compile error, which surfaces as an unreadable stack a long way from
 * the cause. In practice this means iOS before 16.4 and Chrome before 91: not
 * many phones now, but the ones it does mean are exactly the phones whose owner
 * would otherwise be told nothing.
 *
 * The bytes are a minimal module whose body uses `v128.const` and `i32x4.eq`.
 * Validating rather than instantiating keeps it synchronous and free.
 */
const SIMD_PROBE = new Uint8Array([
  0x00, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00, 0x01, 0x05, 0x01, 0x60, 0x00,
  0x01, 0x7b, 0x03, 0x02, 0x01, 0x00, 0x0a, 0x0a, 0x01, 0x08, 0x00, 0x41, 0x00,
  0xfd, 0x0f, 0xfd, 0x62, 0x0b,
]);

export type FaceSupport =
  { supported: true } | { supported: false; reason: string };

let cached: FaceSupport | null = null;

export function faceSupport(): FaceSupport {
  if (cached) return cached;

  cached = (() => {
    if (typeof WebAssembly === "undefined") {
      return {
        supported: false,
        reason: "This browser does not support WebAssembly.",
      } as const;
    }
    if (!WebAssembly.validate(SIMD_PROBE)) {
      return {
        supported: false,
        reason:
          "This browser is too old to group faces. On an iPhone that means iOS 16.4 or newer.",
      } as const;
    }
    // Used to turn a photograph into pixels the model can read. Universally
    // available where the rest of this is, and cheap to be certain about.
    if (typeof createImageBitmap !== "function") {
      return {
        supported: false,
        reason: "This browser cannot decode photos in the background.",
      } as const;
    }
    return { supported: true } as const;
  })();

  return cached;
}

/* ─────────────────────────── Warming the cache ───────────────────────────── */

/** What has to arrive before a single face can be found. */
const MODEL_URLS = [
  "/models/yunet-2023mar.onnx",
  "/models/sface-2021dec-int8.onnx",
];

let warming: Promise<void> | null = null;

/**
 * Whether fetching ten megabytes unprompted is a reasonable thing to do here.
 *
 * `saveData` is somebody explicitly asking not to, and is honoured without
 * qualification. The connection type is a weaker signal and used as one: 2g
 * means the download would take minutes and compete with the photographs they
 * actually came for.
 *
 * Both are absent on Safari, which is most phones this will run on, so the
 * absence has to mean yes — treating "unknown" as "no" would disable the
 * warm-up precisely where the model is slowest to fetch later.
 */
function worthWarming(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;

  if (!connection) return true;
  if (connection.saveData === true) return false;
  return (
    connection.effectiveType !== "slow-2g" && connection.effectiveType !== "2g"
  );
}

/**
 * Pull the models into the HTTP cache, quietly, while nothing else is happening.
 *
 * The *files* and not the sessions. Creating an inference session parses ten
 * megabytes into memory and holds it there for the life of the tab, which is a
 * real cost on a phone and a poor trade for something nobody has asked for yet.
 * The download is the slow part; parsing it afterwards takes a moment and
 * happens only if somebody actually opens People.
 *
 * Safe to call repeatedly — it runs once per tab, and the browser cache means
 * once per device until the files change.
 */
export function warmFaceModels(): Promise<void> {
  if (warming) return warming;
  if (!faceSupport().supported || !worthWarming()) return Promise.resolve();

  warming = (async () => {
    for (const url of MODEL_URLS) {
      try {
        // Low priority: the gallery is loading photographs somebody is looking
        // at, and this must not compete with them for the connection.
        const response = await fetch(url, { priority: "low" } as RequestInit);

        /*
         * Read to the end, and this is the whole point rather than tidiness.
         *
         * `fetch` resolves as soon as the *headers* arrive, so awaiting it and
         * walking away downloads nothing: the body is still streaming, the
         * response is collected, and the browser cancels it. The first version
         * of this "warmed" ten megabytes in five milliseconds, which is exactly
         * what doing nothing looks like.
         *
         * Drained a chunk at a time rather than through `arrayBuffer()`, so
         * ten megabytes never has to exist at once on a phone. Nothing is kept
         * — the point is that the browser cache keeps it.
         */
        const reader = response.body?.getReader();
        if (!reader) {
          await response.arrayBuffer();
          continue;
        }
        for (;;) {
          const { done } = await reader.read();
          if (done) break;
        }
      } catch {
        // Offline, or the request was dropped. People still works; it just
        // downloads then instead of now.
      }
    }
  })();

  return warming;
}

/**
 * Start warming once the page has settled.
 *
 * Deferred twice: after load, and then to an idle moment. Face grouping is not
 * why anybody opened the app, and the first seconds belong to the photographs.
 */
export function scheduleWarmFaceModels(): void {
  if (!faceSupport().supported) return;

  const start = () => {
    const idle = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, options?: object) => number;
      }
    ).requestIdleCallback;

    if (idle) idle(() => void warmFaceModels(), { timeout: 10_000 });
    else window.setTimeout(() => void warmFaceModels(), 4_000);
  };

  if (document.readyState === "complete") start();
  else window.addEventListener("load", start, { once: true });
}
